import { useEffect, useRef } from 'react'
import Uppy from '@uppy/core'
import UppyDashboard from '@uppy/dashboard'
import AwsS3 from '@uppy/aws-s3'
import '@uppy/core/css/style.min.css'
import '@uppy/dashboard/css/style.min.css'

import {
  createMultipartUpload,
  getPresignedPartUrls,
  completeMultipartUpload,
  abortMultipartUpload
} from '../../api/migrations'

const FILE_TYPES = [
  { value: 'SOURCE_DATA', label: 'Source Data Export' },
  { value: 'CONFIG', label: 'Configuration File' },
  { value: 'ATTACHMENT', label: 'Attachment' },
  { value: 'OTHER', label: 'Other' }
]

// Allowed file types for migration uploads
const ALLOWED_FILE_TYPES = [
  '.zip',
  '.pdf',
  'application/zip',
  'application/x-zip-compressed',
  'application/pdf'
]

const FileUploader = ({ migrationId, onUploadComplete, onUploadError }) => {
  const dashboardRef = useRef(null)
  const uppyRef = useRef(null)

  useEffect(() => {
    if (!dashboardRef.current) return

    // Create Uppy instance
    const uppy = new Uppy({
      id: 'migration-file-uploader',
      autoProceed: false,
      restrictions: {
        maxFileSize: 4 * 1024 * 1024 * 1024, // 4GB
        maxNumberOfFiles: 10,
        allowedFileTypes: ALLOWED_FILE_TYPES
      },
      meta: {
        fileType: 'SOURCE_DATA'
      }
    })

    // Add AWS S3 plugin for uploads - always use multipart for consistency
    uppy.use(AwsS3, {
      // Always use multipart - simpler and works for all file sizes
      shouldUseMultipart: () => true,

      // Multipart upload configuration
      async createMultipartUpload(file) {
        const response = await createMultipartUpload(migrationId, {
          file_name: file.name,
          content_type: file.type || 'application/octet-stream'
        })
        return {
          uploadId: response.upload_id,
          key: response.storage_key
        }
      },

      async signPart(file, { uploadId, key, partNumber }) {
        const response = await getPresignedPartUrls(migrationId, uploadId, key, [partNumber])
        return {
          url: response.presigned_urls[String(partNumber)]
        }
      },

      async completeMultipartUpload(file, { uploadId, key, parts }) {
        const result = await completeMultipartUpload(migrationId, uploadId, key, parts, {
          file_name: file.name,
          file_type: file.meta.fileType || 'OTHER',
          file_size: file.size,
          content_type: file.type || 'application/octet-stream',
          description: file.meta.description || null
        })
        return { location: result.storage_key }
      },

      async abortMultipartUpload(file, { uploadId, key }) {
        await abortMultipartUpload(migrationId, uploadId, key)
      }
    })

    uppy.on('complete', (result) => {
      if (result.successful.length > 0) {
        onUploadComplete?.()
      }
      if (result.failed.length > 0) {
        onUploadError?.(result.failed)
      }
    })

    uppy.on('error', (error) => {
      console.error('Uppy error:', error)
      onUploadError?.([{ error }])
    })

    // Add Dashboard plugin
    uppy.use(UppyDashboard, {
      target: dashboardRef.current,
      inline: true,
      proudlyDisplayPoweredByUppy: false,
      showProgressDetails: true,
      showRemoveButtonAfterComplete: true,
      showSelectedFiles: true,
      hideUploadButton: false,
      disableLocalFiles: false,
      height: 350,
      width: '100%',
      theme: 'light',
      note: 'ZIP and PDF files up to 4GB. Drag and drop or click to browse.',
      metaFields: [
        {
          id: 'fileType',
          name: 'File Type',
          render: ({ value, onChange }, h) => {
            return h('select', {
              className: 'uppy-u-reset',
              style: {
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              },
              value: value || 'SOURCE_DATA',
              onChange: (e) => onChange(e.target.value)
            }, FILE_TYPES.map(t =>
              h('option', { value: t.value, key: t.value }, t.label)
            ))
          }
        },
        {
          id: 'description',
          name: 'Description (optional)',
          placeholder: 'Add a description for this file...'
        }
      ]
    })

    uppyRef.current = uppy

    // Cleanup on unmount
    return () => {
      uppy.destroy()
      uppyRef.current = null
    }
  }, [migrationId, onUploadComplete, onUploadError])

  return (
    <div className="file-uploader" ref={dashboardRef} style={{ minHeight: '350px' }} />
  )
}

export default FileUploader
